import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import {
  keepPreviousData,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { portfolioService } from '@/services/portfolioService';
import { fundService } from '@/services/fundService';
import { APIError } from '@/utils/api-client';
import type {
  PortfolioCompany,
  PortfolioCompanyListParams,
  PortfolioCompanyPage,
  PortfolioCompanyDetail,
  PortfolioSummary,
  FundValuationsSummary,
  CreatePortfolioCompanyRequest,
  UpdatePortfolioCompanyRequest,
  CreateValuationRequest,
  CreateFinancialMetricsRequest,
  FinancialMetricsImportResult,
  CreateContactRequest,
  UpdateContactRequest,
  BankAccountWriteRequest,
  PortfolioCompanyBankAccount,
  DeletionEligibility,
} from '@/types/portfolio';
import { FundInvestmentRows } from '@/types/portfolio';
import { CapTableFinality } from '@/types/captable';
import { queryKeys } from './queryKeys';

// Query hooks
export const usePortfolioCompanies = (params?: PortfolioCompanyListParams) => {
  return useApiQuery<PortfolioCompanyPage>(
    queryKeys.portfolio.list({ ...params }),
    () => portfolioService.getPortfolioCompanies(params),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const usePortfolioSummary = (fundId?: string) => {
  return useApiQuery<PortfolioSummary>(
    queryKeys.portfolio.summary(fundId),
    () => portfolioService.getPortfolioSummary(fundId),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useFundValuationsSummary = (fundId: string | undefined) => {
  return useApiQuery<FundValuationsSummary>(
    queryKeys.portfolio.valuationsSummary(fundId!),
    () => portfolioService.getFundValuationsSummary(fundId!),
    {
      enabled: !!fundId,
      staleTime: 0,
    }
  );
};

export const usePortfolioCompany = (companyId: string | undefined) => {
  return useApiQuery<PortfolioCompanyDetail>(
    queryKeys.portfolio.detail(companyId!),
    () => portfolioService.getPortfolioCompany(companyId!),
    {
      enabled: !!companyId,
    }
  );
};

// detail(id) already carries the working figures, so only another finality
// needs its own fetch.
export const usePortfolioCompanyFigures = (
  companyId: string | undefined,
  finality: CapTableFinality
) => {
  return useApiQuery<PortfolioCompanyDetail>(
    queryKeys.portfolio.figures(companyId!, finality),
    () => portfolioService.getPortfolioCompany(companyId!, finality),
    { enabled: !!companyId && finality !== CapTableFinality.WORKING }
  );
};

export const usePortfolioContacts = (companyId: string | undefined) => {
  return useApiQuery(
    queryKeys.portfolio.contacts(companyId!),
    () => portfolioService.getContacts(companyId!),
    {
      enabled: !!companyId,
    }
  );
};

export const usePortfolioFinancialMetricsPaginated = (
  companyId: string | undefined,
  params?: {
    period_type?: string;
    sort_by?: string;
    sort_order?: string;
    limit?: number;
    offset?: number;
    date_from?: string;
    date_to?: string;
  }
) => {
  return useApiQuery(
    queryKeys.portfolio.financialMetricsPaginated(companyId!, params),
    () => portfolioService.getFinancialMetricsPaginated(companyId!, params),
    {
      enabled: !!companyId,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

// Mutation hooks
export const useCreatePortfolioCompany = () => {
  return useApiMutation<
    PortfolioCompany,
    APIError,
    CreatePortfolioCompanyRequest
  >(portfolioService.createPortfolioCompany, {
    invalidateKeys: [queryKeys.portfolio.all],
    successMessage: 'Portfolio company created successfully',
  });
};

export const useUpdatePortfolioCompany = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    PortfolioCompany,
    APIError,
    { companyId: string; data: UpdatePortfolioCompanyRequest },
    { previousCompany: PortfolioCompanyDetail | undefined }
  >(
    ({ companyId, data }) =>
      portfolioService.updatePortfolioCompany(companyId, data),
    {
      onMutate: async ({ companyId, data }) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: queryKeys.portfolio.detail(companyId),
        });

        // Snapshot the previous value
        const previousCompany =
          queryClient.getQueryData<PortfolioCompanyDetail>(
            queryKeys.portfolio.detail(companyId)
          );

        // Optimistically update to the new value
        if (previousCompany) {
          queryClient.setQueryData(queryKeys.portfolio.detail(companyId), {
            ...previousCompany,
            ...data,
          });
        }

        // Return a context object with the snapshotted value
        return { previousCompany };
      },
      onError: (_err, variables, context) => {
        // If the mutation fails, use the context returned from onMutate to rollback
        if (context?.previousCompany) {
          queryClient.setQueryData(
            queryKeys.portfolio.detail(variables.companyId),
            context.previousCompany
          );
        }
      },
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.all,
        queryKeys.portfolio.detail(variables.companyId),
      ],
      successMessage: 'Portfolio company updated successfully',
    }
  );
};

export const useDeletePortfolioCompany = () => {
  return useApiMutation<void, APIError, string>(
    portfolioService.deletePortfolioCompany,
    {
      invalidateKeys: [queryKeys.portfolio.all],
      successMessage: 'Portfolio company deleted successfully',
    }
  );
};

export const useCreateValuation = () => {
  return useApiMutation<
    unknown,
    APIError,
    { companyId: string; data: CreateValuationRequest }
  >(
    ({ companyId, data }) => portfolioService.createValuation(companyId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.detail(variables.companyId),
        queryKeys.portfolio.all,
      ],
      successMessage: 'Valuation created successfully',
    }
  );
};

export const useApproveValuation = () => {
  return useApiMutation<
    unknown,
    APIError,
    { companyId: string; valuationId: string }
  >(
    ({ companyId, valuationId }) =>
      portfolioService.approveValuation(companyId, valuationId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.detail(variables.companyId),
        queryKeys.portfolio.all,
        queryKeys.funds.all,
      ],
      successMessage: 'Valuation approved',
    }
  );
};

export const useCreateFinancialMetrics = () => {
  return useApiMutation<
    unknown,
    APIError,
    { companyId: string; data: CreateFinancialMetricsRequest }
  >(
    ({ companyId, data }) =>
      portfolioService.createFinancialMetrics(companyId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.detail(variables.companyId),
        queryKeys.portfolio.financialMetrics(variables.companyId),
      ],
      successMessage: 'Financial metrics created successfully',
      showErrorToast: false, // Disable generic error toast since we handle 422 validation errors in the form
    }
  );
};

export const useUpdatePortfolioFinancialMetrics = () => {
  return useApiMutation<
    unknown,
    APIError,
    {
      companyId: string;
      metricsId: string;
      data: CreateFinancialMetricsRequest;
    }
  >(
    ({ companyId, metricsId, data }) =>
      portfolioService.updateFinancialMetrics(companyId, metricsId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.detail(variables.companyId),
        queryKeys.portfolio.financialMetrics(variables.companyId),
      ],
      successMessage: 'Financial metrics updated successfully',
      showErrorToast: false, // Disable generic error toast since we handle 422 validation errors in the form
    }
  );
};

export const useDownloadFinancialMetricsTemplate = () => {
  return useApiMutation<void, APIError, string>(
    (companyId) => portfolioService.downloadFinancialMetricsTemplate(companyId),
    {
      showSuccessToast: false, // the browser download is the feedback
      errorMessage: 'Template download failed. Try again.',
    }
  );
};

export const useValidateFinancialMetricsFile = () => {
  return useApiMutation<
    FinancialMetricsImportResult,
    APIError,
    { companyId: string; file: File }
  >(
    ({ companyId, file }) =>
      portfolioService.importFinancialMetrics(companyId, file, 'validate'),
    { showSuccessToast: false }
  );
};

export const useImportFinancialMetrics = () => {
  return useApiMutation<
    FinancialMetricsImportResult,
    APIError,
    { companyId: string; file: File }
  >(
    ({ companyId, file }) =>
      portfolioService.importFinancialMetrics(companyId, file, 'apply'),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.detail(variables.companyId),
        queryKeys.portfolio.financialMetrics(variables.companyId),
      ],
      showSuccessToast: false,
    }
  );
};

export const useCreateContact = () => {
  return useApiMutation<
    unknown,
    APIError,
    { companyId: string; data: CreateContactRequest }
  >(({ companyId, data }) => portfolioService.createContact(companyId, data), {
    invalidateKeys: (_data, variables) => [
      queryKeys.portfolio.contacts(variables.companyId),
      queryKeys.portfolio.detail(variables.companyId),
    ],
    successMessage: 'Contact created successfully',
  });
};

export const useUpdateContact = () => {
  return useApiMutation<
    unknown,
    APIError,
    { companyId: string; contactId: string; data: UpdateContactRequest }
  >(
    ({ companyId, contactId, data }) =>
      portfolioService.updateContact(companyId, contactId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.contacts(variables.companyId),
        queryKeys.portfolio.detail(variables.companyId),
      ],
      successMessage: 'Contact updated successfully',
    }
  );
};

export const useDeleteContact = () => {
  return useApiMutation<
    void,
    APIError,
    { companyId: string; contactId: string }
  >(
    ({ companyId, contactId }) =>
      portfolioService.deleteContact(companyId, contactId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.contacts(variables.companyId),
        queryKeys.portfolio.detail(variables.companyId),
      ],
      successMessage: 'Contact deleted successfully',
    }
  );
};

export const useCreateCompanyFormation = () => {
  return useApiMutation<
    unknown,
    APIError,
    {
      companyId: string;
      data: {
        formation_date: string;
        founder_shares: Array<{
          name: string;
          shares: number;
          stakeholder_type?: string;
        }>;
        share_price: number;
        currency_id: string;
        include_option_pool: boolean;
        option_pool_size?: number;
      };
    }
  >(
    ({ companyId, data }) =>
      portfolioService.createCompanyFormation(companyId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.detail(variables.companyId),
        queryKeys.capTable.company(variables.companyId), // Invalidate all cap table queries for this company
        queryKeys.capTable.all, // Invalidate all cap table queries including snapshots
      ],
      successMessage: 'Company formation created successfully',
    }
  );
};

// Compound hooks for pages
export const usePortfolioPageData = (
  fundId: string | undefined,
  listParams: Omit<PortfolioCompanyListParams, 'fund_id' | 'fund_investments'>
) => {
  // Stakes in other funds belong to the holding fund, not to this directory.
  const companyParams = {
    ...listParams,
    fund_id: fundId,
    fund_investments: FundInvestmentRows.EXCLUDE,
  };

  // Not in useQueries: it creates a new observer per key, which ignores
  // keepPreviousData and unmounts the search box while the page loads.
  const companiesQuery = useApiQuery(
    queryKeys.portfolio.list(companyParams),
    () => portfolioService.getPortfolioCompanies(companyParams),
    {
      staleTime: 2 * 60 * 1000,
      placeholderData: keepPreviousData,
      showErrorToast: false,
    }
  );

  const queries = useQueries({
    queries: [
      {
        queryKey: queryKeys.portfolio.sectors(
          fundId,
          FundInvestmentRows.EXCLUDE
        ),
        queryFn: () =>
          portfolioService.getPortfolioSectors(
            fundId,
            FundInvestmentRows.EXCLUDE
          ),
        staleTime: 2 * 60 * 1000,
      },
      {
        queryKey: queryKeys.portfolio.summary(
          fundId,
          FundInvestmentRows.EXCLUDE
        ),
        queryFn: () =>
          portfolioService.getPortfolioSummary(
            fundId,
            FundInvestmentRows.EXCLUDE
          ),
        staleTime: 2 * 60 * 1000,
      },
      {
        queryKey: queryKeys.funds.list(),
        queryFn: () => fundService.getFunds(),
        staleTime: 5 * 60 * 1000,
      },
    ],
  });

  const [sectorsQuery, summaryQuery, fundsQuery] = queries;

  return {
    companies: companiesQuery.data?.items ?? [],
    totalCount: companiesQuery.data?.total_count ?? 0,
    sectors: sectorsQuery.data ?? [],
    summary: summaryQuery.data ?? null,
    funds: fundsQuery.data ?? [],
    isLoading:
      companiesQuery.isLoading ||
      summaryQuery.isLoading ||
      fundsQuery.isLoading,
    error: companiesQuery.error || summaryQuery.error || fundsQuery.error,
    refetch: () => {
      companiesQuery.refetch();
      sectorsQuery.refetch();
      summaryQuery.refetch();
      fundsQuery.refetch();
    },
  };
};

export const useDeletionEligibility = (
  companyId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  return useApiQuery<DeletionEligibility>(
    queryKeys.portfolio.deletionEligibility(companyId ?? ''),
    () => portfolioService.getDeletionEligibility(companyId!),
    {
      enabled: !!companyId && options.enabled !== false,
      staleTime: 0,
      gcTime: 0,
    }
  );
};

export const useArchivePortfolioCompany = () => {
  return useApiMutation<{ id: string }, APIError, string>(
    portfolioService.archivePortfolioCompany,
    {
      invalidateKeys: [queryKeys.portfolio.all],
      successMessage: 'Portfolio company archived',
    }
  );
};

export const usePortfolioCompanyBankAccounts = (
  companyId: string | undefined
) => {
  return useApiQuery(
    queryKeys.portfolio.bankAccounts(companyId!),
    () => portfolioService.getBankAccounts(companyId!),
    {
      enabled: !!companyId,
    }
  );
};

export const useSyncCompanyBankAccounts = () => {
  return useApiMutation<
    PortfolioCompanyBankAccount[],
    APIError,
    { companyId: string; bankAccounts: BankAccountWriteRequest[] }
  >(
    ({ companyId, bankAccounts }) =>
      portfolioService.syncBankAccounts(companyId, bankAccounts),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.portfolio.bankAccounts(variables.companyId),
      ],
      showSuccessToast: false,
    }
  );
};
